import { apiService } from '@/services/api/api.service';
import type {
  FileFilters,
  FileUploadResponse,
  FileListResponse,
  FileCsvResponse,
} from '../features/files/types/file.types';
interface GetListParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  search?: string;
  onlyMe?: boolean;
  filters?: FileFilters[];
}

export const filesApi = {
  // GET LIST

  getList: async (params: GetListParams = {}): Promise<FileListResponse> => {
    const searchParams = new URLSearchParams();

    if (params.page !== undefined)
      searchParams.append('page', params.page.toString());
    if (params.pageSize !== undefined)
      searchParams.append('pageSize', params.pageSize.toString());
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    if (params.search) searchParams.append('search', params.search);
    if (params.onlyMe !== undefined) searchParams.append('onlyMe', params.onlyMe.toString());
    if (params.filters?.length) {
      params.filters.forEach((filter) => {
        searchParams.append('filters', filter);
      });
    }

    const queryString = searchParams.toString();
    const url = `/p1/get_list${queryString ? `?${queryString}` : ''}`;

    const response: FileListResponse = await apiService.get(url);

    return response;
  },

  downloadCsv: async (): Promise<FileCsvResponse> => {
    const response = await apiService.getBlob('/p1/download_csv');
    return response;
  },

  // UPLOAD LIST

  uploadFile: async (file: File): Promise<FileUploadResponse> => {
    const response = await apiService.upload('/p1/upload', file);
    return response.data;
  },

  // PATCH LIST

  archiveFile: async (fileId: string): Promise<void> => {
    await apiService.patch(`/p1/archive/${fileId}`);
  },

  unarchiveFile: async (fileId: string): Promise<void> => {
    await apiService.patch(`/p1/unarchive/${fileId}`);
  },

  archiveFileMulti: async (fileIds: string[]): Promise<void> => {
    await apiService.patch('/p1/archive_multi', { IDs: fileIds });
  },

  unarchiveFileMulti: async (fileIds: string[]): Promise<void> => {
    await apiService.patch('/p1/unarchive_multi', { IDs: fileIds });
  },

  followFile: async (fileId: string): Promise<void> => {
    await apiService.patch(`/p1/follow/${fileId}`);
  },

  unfollowFile: async (fileId: string): Promise<void> => {
    await apiService.patch(`/p1/unfollow/${fileId}`);
  },

  markAsRead: async (fileId: string): Promise<void> => {
    await apiService.patch(`/p1/mark_as_read/${fileId}`);
  },

  markAsReadMulti: async (fileIds: string[]): Promise<void> => {
    await apiService.patch('/p1/mark_as_read_multi', { IDs: fileIds });
  },

  markAsUnread: async (fileId: string): Promise<void> => {
    await apiService.patch(`/p1/mark_as_unread/${fileId}`);
  },

  markAsUnreadMulti: async (fileIds: string[]): Promise<void> => {
    await apiService.patch('/p1/mark_as_unread_multi', { IDs: fileIds });
  },

  // UPDATE ORDER
  updateFileOrder: async (fileId: string, newOrder: number): Promise<void> => {
    await apiService.put(`/p1/set_order/${fileId}`, { order: newOrder });
  },

  // DELETE LIST

  deleteFile: async (fileId: string): Promise<void> => {
    await apiService.delete(`/p1/delete/${fileId}`);
  },

  deleteFileMulti: async (fileIds: string[]): Promise<void> => {
    await apiService.delete('/p1/delete_multi', { data: { IDs: fileIds } });
  },

  // POST LIST

  addNotes: async ({
    fileId,
    comment,
    fraud,
  }: {
    fileId: string;
    comment: string;
    fraud: boolean;
  }): Promise<void> => {
    await apiService.post(`/p2/set_notes/${fileId}`, { comment, fraud });
  },
};
